import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Pill, Search, ArrowRight, AlertTriangle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "All",
  "SSRI Antidepressant",
  "SNRI Antidepressant",
  "Tricyclic Antidepressant (TCA)",
  "Atypical Antipsychotic",
  "Typical Antipsychotic",
  "Mood Stabilizer",
  "Benzodiazepine",
  "Non-Benzodiazepine Hypnotic",
  "CNS Stimulant",
  "Non-Stimulant ADHD",
  "De-addiction",
  "Anxiolytic",
  "Dementia",
  "Sexual Health",
  "Other",
];

export default function MedicinesPage() {
  const [medicines, setMedicines] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/medicines`).then(res => {
      setMedicines(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = medicines.filter(m => {
    const matchSearch = m.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.brand_names?.some(b => b.toLowerCase().includes(search.toLowerCase())) ||
      m.category?.toLowerCase().includes(search.toLowerCase());
    const matchCat = activeCategory === "All" || m.category?.includes(activeCategory) ||
      (activeCategory === "Benzodiazepine" && m.category?.includes("Benzodiazepine")) ||
      (activeCategory === "De-addiction" && (m.category?.includes("De-addiction") || m.category?.includes("Opioid") || m.category?.includes("Alcohol") || m.category?.includes("Anti-craving"))) ||
      (activeCategory === "Dementia" && (m.category?.includes("Dementia") || m.category?.includes("Cholinesterase") || m.category?.includes("NMDA"))) ||
      (activeCategory === "Sexual Health" && (m.category?.includes("PDE5") || m.category?.includes("Premature"))) ||
      (activeCategory === "Non-Stimulant ADHD" && m.category?.includes("Non-Stimulant")) ||
      (activeCategory === "Anxiolytic" && m.category?.includes("Non-Benzodiazepine") && m.category?.includes("Anxiolytic")) ||
      (activeCategory === "Mood Stabilizer" && m.category?.includes("Mood Stabilizer")) ||
      (activeCategory === "Other" && m.category?.includes("SARI") || m.category?.includes("NDRI") || m.category?.includes("NaSSA") || m.category?.includes("Wakefulness") || m.category?.includes("Anticonvulsant"));
    return matchSearch && matchCat;
  });

  return (
    <div>
      <section className="py-16 sm:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4" data-testid="medicines-heading">
            Psychiatric Medicines Guide
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            A comprehensive, patient-friendly guide to psychiatric medications. Understand what your medication does, how to take it, and what to expect.
          </p>
          <div className="mt-4 flex items-start gap-2 p-4 rounded-lg bg-card border border-primary/20 max-w-2xl">
            <AlertTriangle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Disclaimer:</strong> This information is for educational purposes only. Never start, stop, or change medication without consulting your psychiatrist.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative max-w-md mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="medicines-search"
              placeholder="Search by medicine name or brand..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                data-testid={`med-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  activeCategory === cat
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-muted-foreground border-border hover:border-primary/50"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-16 text-muted-foreground">Loading medicines...</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {filtered.map(m => (
                <Link key={m.slug} to={`/medicines/${m.slug}`} data-testid={`medicine-${m.slug}`}>
                  <Card className="h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer border">
                    <CardContent className="p-5">
                      <span className="text-xs text-muted-foreground uppercase tracking-wider">{m.category}</span>
                      <h3 className="font-heading font-semibold text-base mt-1">{m.name}</h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {m.brand_names?.join(", ")}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{m.what_is_it}</p>
                      <span className="text-sm text-primary font-medium inline-flex items-center gap-1 mt-3">
                        View details <ArrowRight className="w-3 h-3" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-muted-foreground">No medicines match your search.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
